import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  CalendarIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CalculatorIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';
import DateQuickPresets from '../../components/DateQuickPresets';

const DreAnalitico = () => {
  const [dados, setDados] = useState({
    periodo: { inicio: '', fim: '' },
    totais: {
      receitas: 0,
      despesas: 0,
      resultado: 0,
      margem: 0
    },
    planosContas: [], // Estrutura hierárquica com valores
    grupos: [], // Agrupamento por dre_grupo
    lancamentosDetalhados: [] // Lançamentos por plano de conta
  });
  
  const [filtros, setFiltros] = useState({
    data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
    data_fim: format(new Date(), 'yyyy-MM-dd'),
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
    nivel_detalhe: '2' // 1=Grupos, 2=Plano de contas, 3=Lançamentos
  });
  
  const [mostrarFiltros, setMostrarFiltros] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [exportandoPdf, setExportandoPdf] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [planosContasExpandidos, setPlanosContasExpandidos] = useState({});
  const [gruposExpandidos, setGruposExpandidos] = useState({});
  const [visualizacao, setVisualizacao] = useState('hierarquica'); // 'hierarquica' ou 'tabela'

  const getGrupoNome = (entry) => {
    const grupo = entry?.dre_grupo_resolvido || entry?.dre_grupo;
    return grupo && String(grupo).trim() ? grupo : 'Nao Categorizado';
  };

  const getGrupoKey = (entry) => `${entry?.tipo || 'desconhecido'}:${getGrupoNome(entry)}`;

  useEffect(() => {
    carregarDreAnalitico();
    carregarDropdowns();
  }, [filtros]);

  const carregarDreAnalitico = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dre-analitico', { params: filtros });
      setDados(response.data);
      
      // Inicializar estado de expansão para planos de contas
      const expansoes = {};
      response.data.planosContas.forEach(item => {
        if (item.nivel === 1) {
          expansoes[item.id] = true;
        }
      });
      setPlanosContasExpandidos(expansoes);
      
      // Inicializar estado de expansão para grupos
      const gruposExpan = {};
      response.data.grupos.forEach(grupo => {
        gruposExpan[getGrupoKey(grupo)] = true;
      });
      setGruposExpandidos(gruposExpan);
      
    } catch (error) {
      console.error('Erro ao carregar DRE Analítico:', error);
      toast.error('Erro ao carregar DRE Analítico');
    } finally {
      setCarregando(false);
    }
  };

  const carregarDropdowns = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias')
      ]);
      
      setCentrosCustos(centrosResponse.data);
      setContasBancarias(contasResponse.data);
    } catch (error) {
      console.error('Erro ao carregar dropdowns:', error);
    }
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(valor);
  };

  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };

  const togglePlanoConta = (id) => {
    setPlanosContasExpandidos(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleGrupo = (grupo) => {
    setGruposExpandidos(prev => ({
      ...prev,
      [grupo]: !prev[grupo]
    }));
  };

  const limparFiltros = () => {
    setFiltros({
      data_inicio: format(new Date().setMonth(new Date().getMonth() - 1), 'yyyy-MM-dd'),
      data_fim: format(new Date(), 'yyyy-MM-dd'),
      centro_custo_id: 'todos',
      conta_bancaria_id: 'todas',
      nivel_detalhe: '2'
    });
  };

  const exportarPdfAnalitico = async () => {
    setExportandoPdf(true);
    try {
      const response = await axios.get('/api/exportar/pdf/dre-analitico', {
        params: filtros,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `dre_analitico_${filtros.data_inicio}_${filtros.data_fim}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF analitico gerado com sucesso!');
    } catch (error) {
      console.error('Erro ao exportar PDF analitico:', error);
      toast.error('Erro ao gerar PDF analitico');
    } finally {
      setExportandoPdf(false);
    }
  };

  const totalReceitasPeriodo = Number(dados?.totais?.receitas || 0);
  const totalDespesasPeriodo = Number(dados?.totais?.despesas || 0);
  const margemPeriodo = Number(dados?.totais?.margem || 0);
  const totalResumoReceitas = dados.grupos
    .filter((grupo) => grupo.tipo === 'receita')
    .reduce((acc, grupo) => acc + Number(grupo.total || 0), 0);
  const totalResumoDespesas = dados.grupos
    .filter((grupo) => grupo.tipo === 'despesa')
    .reduce((acc, grupo) => acc + Number(grupo.total || 0), 0);
  const diferencaReceitasResumo = totalReceitasPeriodo - totalResumoReceitas;
  const diferencaDespesasResumo = totalDespesasPeriodo - totalResumoDespesas;
  const totalNaoCategorizado = dados.grupos
    .filter((grupo) => getGrupoNome(grupo) === 'Nao Categorizado')
    .reduce((acc, grupo) => acc + Number(grupo.total || 0), 0);
  const lancamentosNaoCategorizados = dados.lancamentosDetalhados.filter((item) => getGrupoNome(item) === 'Nao Categorizado');
  const contasNaoCategorizadas = dados.planosContas
    .filter((item) => getGrupoNome(item) === 'Nao Categorizado' && Math.abs(Number(item.total_grupo || item.total || 0)) > 0.004)
    .sort((a, b) => Number(b.total_grupo || b.total || 0) - Number(a.total_grupo || a.total || 0))
    .slice(0, 6);
  const resumoFechado =
    Math.abs(diferencaReceitasResumo) <= 0.01 &&
    Math.abs(diferencaDespesasResumo) <= 0.01;

  // Buscar subcontas de um plano de conta
  const buscarSubcontas = (parentId, grupoContexto = null) => {
    return dados.planosContas.filter((item) => {
      if (item.parent_id !== parentId) return false;
      if (!grupoContexto) return true;
      return item.tipo === grupoContexto.tipo && getGrupoNome(item) === getGrupoNome(grupoContexto);
    });
  };

  // Buscar lançamentos de um plano de conta
  const buscarLancamentosPorPlanoConta = (planoContaId) => {
    return dados.lancamentosDetalhados.filter(l => l.plano_conta_id === planoContaId);
  };

  // Buscar itens por grupo DRE
  const buscarItensPorGrupo = (grupoNome, tipo) => {
    return dados.planosContas.filter((item) => {
      if (item.tipo !== tipo || getGrupoNome(item) !== grupoNome) return false;
      if (Math.abs(Number(item.total_grupo || item.total || 0)) <= 0.004) return false;
      if (!item.parent_id) return true;

      const parent = dados.planosContas.find((plano) => plano.id === item.parent_id);
      if (!parent) return true;

      return parent.tipo !== tipo || getGrupoNome(parent) !== grupoNome;
    });
  };

  const renderizarPlanoConta = (item, nivel = 0, grupoContexto = null) => {
  const subcontas = buscarSubcontas(item.id, grupoContexto);
  const lancamentos = buscarLancamentosPorPlanoConta(item.id);
  const temSubcontas = subcontas.length > 0;
  const expandido = planosContasExpandidos[item.id];
  const isContaPai = !item.parent_id; // Nova variável
  const grupoNome = getGrupoNome(item);
  const grupoDireto = item.dre_grupo_direto;
  const grupoHerdado = !grupoDireto && grupoNome !== 'Nao Categorizado';
  const grupoNaoConfigurado = grupoNome === 'Nao Categorizado';
  const totalExibicao = Number(grupoContexto ? item.total_grupo || 0 : item.total || 0);
  
  return (
    <div key={item.id} className="mb-1">
      <div 
        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
          item.tipo === 'receita' 
            ? 'bg-green-50 hover:bg-green-100' 
            : 'bg-red-50 hover:bg-red-100'
        }`}
        style={{ marginLeft: `${nivel * 24}px` }}
        onClick={() => temSubcontas && togglePlanoConta(item.id)}
      >
        <div className="flex items-center flex-1">
          {temSubcontas ? (
            <button className="mr-2 focus:outline-none">
              {expandido ? (
                <ChevronDownIcon className="h-4 w-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="h-4 w-4 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-6"></div>
          )}
          
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 ${
              item.tipo === 'receita' ? 'bg-green-500' : 'bg-red-500'
            }`}></div>
            <div>
              <div className="font-medium text-gray-900">
                {item.codigo} - {item.descricao}
                {isContaPai && <span className="ml-2 text-xs text-blue-600">(CONTA PAI)</span>}
              </div>
              {grupoNome && (
                <div className="text-xs text-gray-500 mt-1">
                  Grupo: {grupoNome}
                  {grupoHerdado && (
                    <span className="ml-2 text-amber-600">herdado da hierarquia</span>
                  )}
                  {grupoNaoConfigurado && (
                    <span className="ml-2 text-danger-600">sem grupo nesta arvore</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="text-right">
          <div className={`font-bold ${
            item.tipo === 'receita' ? 'text-green-600' : 'text-red-600'
          }`}>
            {formatarValor(totalExibicao)}
          </div>
          
          {/* Mostrar detalhes do cálculo se disponível */}
          {grupoContexto ? (
            <div className="text-xs text-gray-500 mt-1">
              {Math.abs(Number(item.total || 0) - totalExibicao) > 0.004 ? (
                <>
                  <span className="text-gray-600">Na estrutura completa: </span>
                  {formatarValor(item.total || 0)}
                </>
              ) : (
                <span className="text-gray-600">Total integralmente neste grupo</span>
              )}
            </div>
          ) : item.total_direto !== undefined && item.total_hierarquico !== undefined && (
            <div className="text-xs text-gray-500 mt-1">
              {isContaPai ? (
                <>
                  <span className="text-blue-600">Soma hierárquica: </span>
                  {formatarValor(item.total_hierarquico)}
                  {item.total_direto > 0 && (
                    <span className="ml-1">(+ {formatarValor(item.total_direto)} direto)</span>
                  )}
                </>
              ) : (
                <>
                  <span className="text-gray-600">Valor direto: </span>
                  {formatarValor(item.total_direto)}
                </>
              )}
            </div>
          )}
          
          {/* Porcentagens - SÓ para contas que não são pai (evitar duplicação) */}
          {!isContaPai && totalReceitasPeriodo > 0 && item.tipo === 'receita' && (
            <div className="text-xs text-gray-500 mt-1">
              {((totalExibicao / totalReceitasPeriodo) * 100).toFixed(1)}% das receitas
            </div>
          )}
          {!isContaPai && totalDespesasPeriodo > 0 && item.tipo === 'despesa' && (
            <div className="text-xs text-gray-500 mt-1">
              {((totalExibicao / totalDespesasPeriodo) * 100).toFixed(1)}% das despesas
            </div>
          )}
        </div>
      </div>
      
      {/* Subcontas */}
      {expandido && temSubcontas && (
        <div className="ml-6 border-l-2 border-gray-200 pl-2">
          {subcontas.map(subconta => 
            renderizarPlanoConta(subconta, nivel + 1, grupoContexto)
          )}
        </div>
      )}
      
      {/* Lançamentos detalhados */}
      {filtros.nivel_detalhe === '3' && expandido && lancamentos.length > 0 && (
        <div className="ml-12 mt-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Lançamentos Detalhados
          </div>
          <div className="space-y-1">
            {lancamentos.map(lancamento => (
              <div key={lancamento.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {lancamento.descricao}
                  </div>
                  <div className="text-xs text-gray-500">
                    {format(parseISO(lancamento.data), 'dd/MM/yyyy')} • 
                    {lancamento.centro_custo_descricao && ` • ${lancamento.centro_custo_descricao}`}
                  </div>
                </div>
                <div className={`text-sm font-medium ${
                  lancamento.tipo === 'receita' ? 'text-green-600' : 'text-red-600'
                }`}>
                  {formatarValor(lancamento.valor)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

  // Renderizar por grupos DRE
  const renderizarPorGrupos = () => {
    return dados.grupos.map((grupo) => {
      const grupoKey = getGrupoKey(grupo);
      const itensGrupo = buscarItensPorGrupo(getGrupoNome(grupo), grupo.tipo);
      const expandido = gruposExpandidos[grupoKey];
      
      return (
        <div key={grupoKey} className="mb-4">
          <div 
            className="flex items-center justify-between p-4 bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200"
            onClick={() => toggleGrupo(grupoKey)}
          >
            <div className="flex items-center">
              <button className="mr-3 focus:outline-none">
                {expandido ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-600" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-600" />
                )}
              </button>
              <div>
                <div className="font-semibold text-gray-900 text-lg">
                  {getGrupoNome(grupo)}
                </div>
                <div className="text-sm text-gray-600">
                  {grupo.tipo === 'receita' ? 'Grupo de Receitas' : 'Grupo de Despesas'} | 
                  {itensGrupo.length} conta{itensGrupo.length !== 1 ? 's' : ''}
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <div className={`text-xl font-bold ${
                grupo.tipo === 'receita' ? 'text-green-600' : 'text-red-600'
              }`}>
                {formatarValor(grupo.total)}
              </div>
              {grupo.tipo === 'receita' && (
                <div className="text-sm text-gray-600">
                  {((grupo.total / totalReceitasPeriodo) * 100).toFixed(1)}% das receitas
                </div>
              )}
              {grupo.tipo === 'despesa' && (
                <div className="text-sm text-gray-600">
                  {((grupo.total / totalDespesasPeriodo) * 100).toFixed(1)}% das despesas
                </div>
              )}
            </div>
          </div>
          
          {expandido && (
            <div className="mt-2 ml-8">
              {itensGrupo.map((item) => renderizarPlanoConta(item, 1, grupo))}
            </div>
          )}
        </div>
      );
    });
  };

  // Renderizar em tabela
  const renderizarTabela = () => {
    // Filtrar apenas contas raiz para a tabela (ou você pode mostrar todas)
    const contasParaTabela = dados.planosContas.filter(item => !item.parent_id);
    
    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Código
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Descrição
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tipo
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Grupo DRE
              </th>
              <th scope="col" className="px6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Detalhes
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                % Geral
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {contasParaTabela.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {item.codigo}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {item.descricao}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    item.tipo === 'receita' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.tipo === 'receita' ? 'Receita' : 'Despesa'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className={getGrupoNome(item) === 'Nao Categorizado' ? 'text-sm text-danger-600 font-medium' : 'text-sm text-gray-600'}>
                    {getGrupoNome(item)}
                  </div>
                  {!item.dre_grupo_direto && getGrupoNome(item) !== 'Nao Categorizado' && (
                    <div className="text-xs text-amber-600">Herdado da hierarquia</div>
                  )}
                  {getGrupoNome(item) === 'Nao Categorizado' && (
                    <div className="text-xs text-danger-600">Revisar cadastro do plano de contas</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-bold ${
                    item.tipo === 'receita' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {formatarValor(item.total || 0)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {item.total_direto !== undefined && item.total_hierarquico !== undefined && (
                      <span className="text-xs">
                        {item.total_direto > 0 ? `${formatarValor(item.total_direto)} direto + ` : ''}
                        {formatarValor(item.total_hierarquico - (item.total_direto || 0))} subcontas
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {item.tipo === 'receita' && totalReceitasPeriodo > 0
                      ? `${((item.total / totalReceitasPeriodo) * 100).toFixed(1)}%`
                      : item.tipo === 'despesa' && totalDespesasPeriodo > 0
                      ? `${((item.total / totalDespesasPeriodo) * 100).toFixed(1)}%`
                      : '0%'}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">📊 DRE Analítico</h1>
          <p className="text-gray-600">Análise detalhada por plano de contas</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            className="btn-secondary"
          >
            <FunnelIcon className="h-5 w-5 mr-2" />
            {mostrarFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
          <button
            onClick={exportarPdfAnalitico}
            disabled={exportandoPdf}
            className="btn-primary"
          >
            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
            {exportandoPdf ? 'Gerando PDF...' : 'Exportar PDF'}
          </button>
        </div>
      </div>

      {/* Filtros */}
      {mostrarFiltros && (
        <div className="card mb-6 animate-slide-in">
          <div className="card-body">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">🔍 Filtros do Relatório</h3>
              <button
                onClick={() => setMostrarFiltros(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <DateQuickPresets
              mode="range"
              startDate={filtros.data_inicio}
              endDate={filtros.data_fim}
              onSelect={({ startDate, endDate }) =>
                setFiltros((old) => ({ ...old, data_inicio: startDate, data_fim: endDate }))
              }
              className="mb-4"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="form-group">
                <label className="form-label">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Data Início
                </label>
                <input
                  type="date"
                  value={filtros.data_inicio}
                  onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">
                  <CalendarIcon className="h-4 w-4 inline mr-1" />
                  Data Fim
                </label>
                <input
                  type="date"
                  value={filtros.data_fim}
                  onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })}
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label className="form-label">Centro de Custo</label>
                <select
                  value={filtros.centro_custo_id}
                  onChange={(e) => setFiltros({ ...filtros, centro_custo_id: e.target.value })}
                  className="form-select"
                >
                  <option value="todos">Todos os Centros</option>
                  {centrosCustos.map(centro => (
                    <option key={centro.id} value={centro.id}>
                      {centro.codigo} - {centro.descricao}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label className="form-label">Conta Bancária</label>
                <select
                  value={filtros.conta_bancaria_id}
                  onChange={(e) => setFiltros({ ...filtros, conta_bancaria_id: e.target.value })}
                  className="form-select"
                >
                  <option value="todas">Todas as Contas</option>
                  {contasBancarias.map(conta => (
                    <option key={conta.id} value={conta.id}>
                      {conta.banco} - {conta.conta}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group md:col-span-2">
                <label className="form-label">Nível de Detalhe</label>
                <div className="flex space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="1"
                      checked={filtros.nivel_detalhe === '1'}
                      onChange={(e) => setFiltros({ ...filtros, nivel_detalhe: e.target.value })}
                      className="form-radio"
                    />
                    <span className="ml-2">Grupos DRE</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="2"
                      checked={filtros.nivel_detalhe === '2'}
                      onChange={(e) => setFiltros({ ...filtros, nivel_detalhe: e.target.value })}
                      className="form-radio"
                    />
                    <span className="ml-2">Plano de Contas</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      value="3"
                      checked={filtros.nivel_detalhe === '3'}
                      onChange={(e) => setFiltros({ ...filtros, nivel_detalhe: e.target.value })}
                      className="form-radio"
                    />
                    <span className="ml-2">Lançamentos</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex justify-between">
              <button
                onClick={limparFiltros}
                className="btn-secondary"
              >
                Limpar Filtros
              </button>
              <div className="space-x-3">
                <button
                  onClick={() => setMostrarFiltros(false)}
                  className="btn-secondary"
                >
                  Fechar
                </button>
                <button
                  onClick={carregarDreAnalitico}
                  disabled={carregando}
                  className="btn-primary"
                >
                  {carregando ? (
                    <>
                      <div className="loader h-4 w-4 mr-2"></div>
                      Carregando...
                    </>
                  ) : (
                    <>
                      <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                      Gerar Relatório
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="card bg-success-50 border-success-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-success-100">
                <ArrowTrendingUpIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-success-600">Total Receitas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarValor(totalReceitasPeriodo)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-danger-50 border-danger-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-danger-100">
                <ArrowTrendingDownIcon className="h-6 w-6 text-danger-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-danger-600">Total Despesas</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarValor(totalDespesasPeriodo)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-primary-50 border-primary-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-100">
                <CalculatorIcon className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-primary-600">Resultado</p>
                <p className={`text-2xl font-bold ${
                  (totalReceitasPeriodo - totalDespesasPeriodo) >= 0 ? 'text-success-600' : 'text-danger-600'
                }`}>
                  {formatarValor(totalReceitasPeriodo - totalDespesasPeriodo)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card bg-warning-50 border-warning-200">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-warning-100">
                <ChartBarIcon className="h-6 w-6 text-warning-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-warning-600">Margem</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatarPercentual(margemPeriodo)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controles de Visualização - Só mostra se tiver dados */}
      {!carregando && dados.planosContas && dados.planosContas.length > 0 && (
        <div className="card mb-6">
          <div className="card-body">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold">
                  {visualizacao === 'hierarquica' ? '📐 Visualização Hierárquica' : '📊 Visualização em Tabela'}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Período: {format(parseISO(dados.periodo.inicio || new Date().toISOString()), 'dd/MM/yyyy', { locale: ptBR })} 
                  {' a '}
                  {format(parseISO(dados.periodo.fim || new Date().toISOString()), 'dd/MM/yyyy', { locale: ptBR })}
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setVisualizacao('hierarquica')}
                  className={`px-4 py-2 rounded-md ${
                    visualizacao === 'hierarquica'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <DocumentTextIcon className="h-4 w-4 inline mr-2" />
                  Hierárquica
                </button>
                <button
                  onClick={() => setVisualizacao('tabela')}
                  className={`px-4 py-2 rounded-md ${
                    visualizacao === 'tabela'
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <TableCellsIcon className="h-4 w-4 inline mr-2" />
                  Tabela
                </button>
              </div>
            </div>
            
            <div className="mt-4 text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                  <span>Receitas</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                  <span>Despesas</span>
                </div>
                <div className="flex items-center">
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 mr-2" />
                  <span>Clique para expandir/recolher</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Conteúdo do Relatório */}
      {carregando ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="loader h-12 w-12 mx-auto mb-4"></div>
            <p className="text-lg font-medium text-gray-900">Carregando DRE Analítico...</p>
            <p className="text-gray-600">Aguarde enquanto processamos os dados</p>
          </div>
        </div>
      ) : dados.planosContas && dados.planosContas.length > 0 ? (
        <div className="card">
          <div className="card-body">
            {visualizacao === 'hierarquica' ? (
              <div>
                {/* Visão por Grupos */}
                {filtros.nivel_detalhe === '1' ? (
                  <div>
                    <div className="flex items-center mb-4">
                      <ChartBarIcon className="h-5 w-5 text-primary-600 mr-2" />
                      <h4 className="text-lg font-semibold">Agrupamento por Grupos DRE</h4>
                    </div>
                    {renderizarPorGrupos()}
                  </div>
                ) : (
                  <div>
                    {/* Visão Completa Hierárquica */}
                    <div className="flex items-center mb-4">
                      <DocumentTextIcon className="h-5 w-5 text-primary-600 mr-2" />
                      <h4 className="text-lg font-semibold">Estrutura Completa do Plano de Contas</h4>
                    </div>
                    
                    {/* Separar Receitas e Despesas */}
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <ArrowTrendingUpIcon className="h-5 w-5 text-green-600 mr-2" />
                        <h5 className="text-lg font-semibold text-green-700">RECEITAS</h5>
                      </div>
                      {dados.planosContas
                        .filter(item => item.tipo === 'receita' && !item.parent_id)
                        .map(item => renderizarPlanoConta(item, 0))}
                    </div>
                    
                    <div className="mb-8">
                      <div className="flex items-center mb-4">
                        <ArrowTrendingDownIcon className="h-5 w-5 text-red-600 mr-2" />
                        <h5 className="text-lg font-semibold text-red-700">DESPESAS</h5>
                      </div>
                      {dados.planosContas
                        .filter(item => item.tipo === 'despesa' && !item.parent_id)
                        .map(item => renderizarPlanoConta(item, 0))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Visualização em Tabela
              renderizarTabela()
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="card-body text-center py-12">
            <TableCellsIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-lg font-medium text-gray-900">Nenhum dado encontrado</p>
            <p className="text-gray-600">Nenhum lançamento encontrado para os filtros selecionados</p>
            <button
              onClick={limparFiltros}
              className="btn-primary mt-4"
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Resumo Final - Só mostra se tiver dados */}
      {!carregando && dados.planosContas && dados.planosContas.length > 0 && (
        <div className="card mt-6">
          <div className="card-body">
            <div className="bg-gray-50 rounded-lg p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <CalculatorIcon className="h-6 w-6 text-primary-600 mr-3" />
                  <div>
                    <h4 className="text-xl font-bold text-gray-900">RESUMO DO PERÍODO</h4>
                    <p className="text-sm text-gray-600">
                      Conferencia baseada no grupo DRE resolvido pela hierarquia do plano de contas
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold ${
                    (totalReceitasPeriodo - totalDespesasPeriodo) >= 0 ? 'text-success-600' : 'text-danger-600'
                  }`}>
                    {formatarValor(totalReceitasPeriodo - totalDespesasPeriodo)}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Margem: {formatarPercentual(margemPeriodo)}
                  </div>
                </div>
              </div>

              <div className={`mt-6 rounded-lg border p-4 ${
                totalNaoCategorizado > 0
                  ? 'border-warning-200 bg-warning-50'
                  : resumoFechado
                  ? 'border-success-200 bg-success-50'
                  : 'border-danger-200 bg-danger-50'
              }`}>
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h5 className="font-semibold text-gray-900">Conferencia da classificacao DRE</h5>
                    <p className="text-sm text-gray-600 mt-1">
                      O resumo abaixo usa a mesma classificacao resolvida da estrutura principal.
                    </p>
                  </div>
                  <div className={`text-sm font-medium ${
                    totalNaoCategorizado > 0
                      ? 'text-warning-700'
                      : resumoFechado
                      ? 'text-success-700'
                      : 'text-danger-700'
                  }`}>
                    {totalNaoCategorizado > 0
                      ? 'Existem valores ainda sem grupo herdavel'
                      : resumoFechado
                      ? 'Resumo conferido com sucesso'
                      : 'Existe divergencia entre totais e grupos'}
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="rounded-lg bg-white/70 p-3">
                    <div className="text-gray-500">Receitas: total x resumo</div>
                    <div className="font-semibold text-gray-900 mt-1">
                      {formatarValor(totalReceitasPeriodo)} x {formatarValor(totalResumoReceitas)}
                    </div>
                    <div className={Math.abs(diferencaReceitasResumo) <= 0.01 ? 'text-success-700 mt-1' : 'text-danger-700 mt-1'}>
                      Diferenca: {formatarValor(diferencaReceitasResumo)}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/70 p-3">
                    <div className="text-gray-500">Despesas: total x resumo</div>
                    <div className="font-semibold text-gray-900 mt-1">
                      {formatarValor(totalDespesasPeriodo)} x {formatarValor(totalResumoDespesas)}
                    </div>
                    <div className={Math.abs(diferencaDespesasResumo) <= 0.01 ? 'text-success-700 mt-1' : 'text-danger-700 mt-1'}>
                      Diferenca: {formatarValor(diferencaDespesasResumo)}
                    </div>
                  </div>

                  <div className="rounded-lg bg-white/70 p-3">
                    <div className="text-gray-500">Nao categorizado</div>
                    <div className="font-semibold text-gray-900 mt-1">
                      {formatarValor(totalNaoCategorizado)}
                    </div>
                    <div className={totalNaoCategorizado > 0 ? 'text-warning-700 mt-1' : 'text-success-700 mt-1'}>
                      {lancamentosNaoCategorizados.length} lancamento(s) para revisar
                    </div>
                  </div>
                </div>

                {contasNaoCategorizadas.length > 0 && (
                  <div className="mt-4">
                    <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                      Principais contas para revisao
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                      {contasNaoCategorizadas.map((item) => (
                        <div key={item.id} className="rounded-md bg-white/80 px-3 py-2 text-sm">
                          <div className="font-medium text-gray-900">
                            {item.codigo} - {item.descricao}
                          </div>
                          <div className="text-danger-700">
                            {formatarValor(item.total_grupo || item.total || 0)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Distribuição de Receitas</h5>
                  <div className="space-y-2">
                    {dados.grupos
                      .filter(g => g.tipo === 'receita')
                      .map(grupo => (
                        <div key={getGrupoKey(grupo)} className="flex justify-between items-center">
                          <span className={getGrupoNome(grupo) === 'Nao Categorizado' ? 'text-sm text-danger-600 font-medium' : 'text-sm text-gray-600'}>
                            {getGrupoNome(grupo)}
                          </span>
                          <div className="flex items-center">
                            <span className="font-medium mr-2">
                              {formatarValor(grupo.total)}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({totalReceitasPeriodo > 0 ? ((grupo.total / totalReceitasPeriodo) * 100).toFixed(1) : 0}%)
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
                
                <div>
                  <h5 className="font-semibold text-gray-900 mb-2">Distribuição de Despesas</h5>
                  <div className="space-y-2">
                    {dados.grupos
                      .filter(g => g.tipo === 'despesa')
                      .map(grupo => (
                        <div key={getGrupoKey(grupo)} className="flex justify-between items-center">
                          <span className={getGrupoNome(grupo) === 'Nao Categorizado' ? 'text-sm text-danger-600 font-medium' : 'text-sm text-gray-600'}>
                            {getGrupoNome(grupo)}
                          </span>
                          <div className="flex items-center">
                            <span className="font-medium mr-2">
                              {formatarValor(grupo.total)}
                            </span>
                            <span className="text-xs text-gray-500">
                              ({totalDespesasPeriodo > 0 ? ((grupo.total / totalDespesasPeriodo) * 100).toFixed(1) : 0}%)
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DreAnalitico;
